import { formatDate } from '@/lib/formatters'
import { capitalize } from '@/lib/utils'
import type {
  BookingDocument,
  DocumentType,
  HeaderBookingRef,
  HeaderContainerRollup,
  HeaderReviewRollup,
  HeaderScheduleItem,
  HeaderStatusRollup,
  ReviewOutcome,
  ShipmentHeaderFacts,
} from './types'

export const TABS = ['Overview', 'Documents', 'Threads', 'Timeline'] as const
export type TabName = typeof TABS[number]

// Mirrors Booking::ACTIVE_STATUSES; used only to pick a live-rate context booking.
export const ACTIVE_BOOKING_STATUSES = new Set(['confirmed', 'si_submitted', 'bl_released'])

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  si: 'SI',
  bl: 'BL',
  other: 'Other',
}

export const REVIEW_OUTCOME_LABELS: Record<ReviewOutcome, string> = {
  unreviewed: 'Unreviewed',
  needs_revision: 'Needs revision',
  accepted: 'Accepted',
}

export const REVIEW_OUTCOME_BADGES: Record<ReviewOutcome, string> = {
  unreviewed: 'bg-amber-50 text-amber-700 border-amber-200',
  needs_revision: 'bg-red-50 text-red-700 border-red-200',
  accepted: 'bg-green-50 text-green-700 border-green-200',
}

export const DOCUMENT_REVIEW_SORT_PRIORITY: Record<ReviewOutcome, number> = {
  unreviewed: 0,
  accepted: 1,
  needs_revision: 2,
}

export function documentTypeLabel(documentType: DocumentType) {
  return DOCUMENT_TYPE_LABELS[documentType] || documentType
}

export function documentTypePillClassName(documentType: DocumentType) {
  if (documentType === 'si') return 'bg-blue-50 text-blue-700'
  if (documentType === 'bl') return 'bg-emerald-50 text-emerald-700'
  return 'bg-gray-100 text-gray-600'
}

export function compareDocumentsByRecency(a: { created_at: string; id: string }, b: { created_at: string; id: string }) {
  const dateDiff = new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  if (dateDiff !== 0) return dateDiff
  return b.id.localeCompare(a.id)
}

type BookingDocumentRow = {
  doc: BookingDocument
  docType: DocumentType
  priority: number
}

export function overviewBookingDocumentRows(siDocs: BookingDocument[], blDocs: BookingDocument[], otherDocs: BookingDocument[]) {
  const rows = new Map<string, BookingDocumentRow>()

  function include(doc: BookingDocument | undefined, docType: DocumentType, priority: number) {
    if (!doc) return

    const existing = rows.get(doc.id)
    rows.set(doc.id, {
      doc,
      docType,
      priority: existing ? Math.min(existing.priority, priority) : priority,
    })
  }

  const reviewableDocs = [
    ...siDocs.map((doc) => ({ doc, docType: 'si' as const })),
    ...blDocs.map((doc) => ({ doc, docType: 'bl' as const })),
  ]

  reviewableDocs.forEach(({ doc, docType }) => {
    if (doc.review_outcome === 'accepted') include(doc, docType, 0)
  })

  include(siDocs.find((doc) => doc.is_latest) || siDocs[0], 'si', 1)
  include(blDocs.find((doc) => doc.is_latest) || blDocs[0], 'bl', 1)
  otherDocs.forEach((doc) => include(doc, 'other', 2))

  return Array.from(rows.values()).sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority

    const aTypePriority = documentTypeOverviewPriority(a.docType)
    const bTypePriority = documentTypeOverviewPriority(b.docType)
    if (aTypePriority !== bTypePriority) return aTypePriority - bTypePriority

    return compareDocumentsByRecency(a.doc, b.doc)
  })
}

function documentTypeOverviewPriority(documentType: DocumentType) {
  if (documentType === 'si') return 0
  if (documentType === 'bl') return 1
  return 2
}

export type HeaderBookingStatusItem = {
  status: string
  bookingRef: HeaderBookingRef
}

export function flattenBookingStatusItems(rollups: HeaderStatusRollup[]): HeaderBookingStatusItem[] {
  return rollups.flatMap((rollup) => (
    rollup.booking_refs.map((bookingRef) => ({
      status: rollup.status,
      bookingRef,
    }))
  ))
}

export function formatBookingRowStatusLabel(status: string) {
  if (status === 'draft') return 'Draft'
  if (status === 'quoted') return 'Quoted'
  if (status === 'accepted') return 'Accepted'
  if (status === 'confirmed') return 'Confirmed'
  if (status === 'si_submitted') return 'SI submitted'
  if (status === 'bl_released') return 'BL released'
  if (status === 'cancelled') return 'Cancelled'
  if (status === 'transferred') return 'Transferred'
  return capitalize(status.replace(/_/g, ' '))
}

export function formatScheduleHeaderValue(item: HeaderScheduleItem) {
  return `${item.label} ${formatScheduleValue(item)}`
}

export function formatAttentionSummary(count: number) {
  if (count === 0) return 'Clear'
  return `${count} open ${count === 1 ? 'item' : 'items'}`
}

export function formatUntaggedDocumentSummary(count: number) {
  return `${count} untagged ${count === 1 ? 'document' : 'documents'}`
}

export function formatBookingCountSummary(facts: ShipmentHeaderFacts) {
  if (facts.total_booking_count === 0) return 'No bookings'
  return `${facts.total_booking_count} ${facts.total_booking_count === 1 ? 'booking' : 'bookings'}`
}

export function formatCompactReviewLabel(rollup: HeaderReviewRollup) {
  const documentLabel = rollup.document_type.toUpperCase()
  if (rollup.review_outcome === 'unreviewed') return `${documentLabel} unreviewed`
  if (rollup.review_outcome === 'needs_revision') return `${documentLabel} needs revision`
  return rollup.label.toLowerCase()
}

export function formatContainerPrimarySummary(rollups: HeaderContainerRollup[]) {
  if (rollups.length === 0) return 'No equipment yet'
  return rollups.map(formatContainerPrimaryRollup).join(' · ')
}

export function formatContainerBookingScope(rollups: HeaderContainerRollup[]) {
  const bookingCount = rollups.reduce((total, rollup) => total + rollup.booking_count, 0)
  if (bookingCount === 0) return undefined
  return `across ${bookingCount} ${bookingCount === 1 ? 'booking' : 'bookings'}`
}

export function formatScheduleRefs(item: HeaderScheduleItem) {
  const refs = item.booking_refs.map((ref) => ref.internal_reference).join(', ')
  if (item.source === 'booking') return refs
  return `${formatScheduleSource(item.source)} · ${refs}`
}

function formatContainerPrimaryRollup(rollup: HeaderContainerRollup) {
  if (rollup.container_count > 0 && rollup.unknown_count_booking_count > 0) {
    return `${rollup.container_count} x ${rollup.display_label} + ${rollup.unknown_count_booking_count} count unknown`
  }
  if (rollup.container_count > 0) {
    return `${rollup.container_count} x ${rollup.display_label}`
  }
  return `${rollup.display_label} - count unknown`
}

export function formatScheduleValue(item: HeaderScheduleItem) {
  if (item.unit === 'days') return `${item.value} days`
  if (item.key === 'sailing_date' || item.key === 'eta') return formatDate(String(item.value))
  return String(item.value)
}

function formatScheduleSource(source: HeaderScheduleItem['source']) {
  if (source === 'quote_snapshot') return 'quote snapshot'
  if (source === 'offer_schedule') return 'offer schedule'
  return source
}

export function formatBookingMoveOption(booking: { internal_reference: string | null; carrier_booking_id: string | null; carrier_name: string | null; carrier: string | null }) {
  const carrier = booking.carrier_name || booking.carrier || 'Unknown'
  return `${booking.internal_reference || booking.carrier_booking_id || 'Unreferenced booking'} — ${carrier}`
}
